import {
  EntityDescribedProperty,
  DeletedProperty,
  EntityDescribedPropertyCategory,
  EntityDescribedPropertyFormat,
} from '../../../../../types';
import * as MockHook from '../../../../util/useMockHook';
import { useAnchorElState } from 'react-use-object-state';
import useEntityDrawer from '../useEntityDrawer';

export interface EntityDrawerPropertyLogicProps {
  property: EntityDescribedProperty | DeletedProperty;
}
const useEntityDrawerPropertyLogic = ({
  property,
}: EntityDrawerPropertyLogicProps) => {
  const menu = useAnchorElState(null);
  const {
    entity: { updateEntity, entity },
    propertyFindRelated,
    propertyFindSimilar,
    propertyActions,
  } = useEntityDrawer();

  const isEntityDescribedProperty = (
    property: EntityDescribedProperty | DeletedProperty
  ): property is EntityDescribedProperty => {
    return (property as EntityDescribedProperty).source !== undefined;
  };

  const overridden = !(
    property.originalValue === undefined ||
    property.originalValue === null ||
    property.originalValue === '' ||
    (typeof property.originalValue === 'string' &&
      property.originalValue.charCodeAt(0) === 8203)
  );

  let edited: boolean;
  let deleted: boolean;
  let label: string;
  let handleAction: (onClick: any) => VoidFunction;

  if (isEntityDescribedProperty(property)) {
    edited = property.edited;
    deleted = false;
    label =
      overridden || entity?._source === 'integration-managed'
        ? 'Modified'
        : 'Enriched';
    handleAction = (onClick: any) => () => {
      onClick(property.name, property.value, entity?._class[0]);
    };
  } else {
    edited = false;
    deleted = true;
    label = 'Removed';
    handleAction = (onClick: any) => () => {
      onClick(property.name, property.originalValue, entity?._class[0]);
    };
  }

  const resetPropertyValue = () => {
    void updateEntity?.(Date.now(), {
      [property.name]: property.originalValue,
    });
  };

  return {
    menu,
    name: property.name,
    deleted,
    originalValue: property.originalValue,
    label,
    overridden,
    category: property.category,
    edited,
    property,
    propertyFindRelated,
    propertyFindSimilar,
    propertyActions,
    updateEntity,
    handleAction,
    resetPropertyValue,
    isEntityDescribedProperty,
  };
};

export type EntityDrawerPropertyComponentProps = ReturnType<
  typeof useEntityDrawerPropertyLogic
>;

export const MOCK_ENTITY_DRAWER_PROPERTY_PROPS: EntityDrawerPropertyComponentProps = {
  menu: {
    clearAnchorEl: MockHook.mockFunction,
    setAnchorEl: MockHook.mockFunction,
    anchorEl: null,
  },
  name: 'nickname',
  deleted: false,
  originalValue: '',
  label: 'Removed',
  overridden: false,
  category: EntityDescribedPropertyCategory.PROPERTY,
  edited: false,
  property: {
    name: 'string',
    description: 'string',
    required: false,
    source: 'string',
    type: EntityDescribedPropertyFormat.STRING,
    value: 'any',
    originalValue: '',
    category: EntityDescribedPropertyCategory.PROPERTY,
    edited: false,
    isSchemaOnly: false,
  },
  propertyFindRelated: true,
  propertyFindSimilar: true,
  propertyActions: [],
  updateEntity: MockHook.mockFunction,
  handleAction: MockHook.mockFunction,
  resetPropertyValue: MockHook.mockFunction,
  isEntityDescribedProperty: MockHook.mockFunction,
};

export default MockHook.useMockHook(
  useEntityDrawerPropertyLogic,
  MOCK_ENTITY_DRAWER_PROPERTY_PROPS
);
